/**
 * SalesRagonePlot — ECharts scatter for the cell-selection flow. The whole
 * Insights market is ALWAYS on screen (seeded client-side from the same
 * catalogue the Explorer Ragone uses) and rendered with the SAME visual design
 * as the Explorer graph — cells coloured by chemistry, a category legend, the
 * Batemo watermark, the same axes/grid. The backend ranking
 * (rank_cells_for_application → plot_data) then PAINTS the flow state on top:
 *
 *   - in play (not yet ruled out / still a candidate) → chemistry colour
 *   - ruled out      passed === false                 → greyed, STAYS visible
 *   - not a candidate (overlay exists, cell not ranked)→ greyed, STAYS visible
 *   - top pick       rank <= top_n AND stage shortlist → chemistry colour + white
 *                                                        ring + glow + label
 *   - manual pin     user-picked (overrides exclusion) → orange pin on top
 *
 * BEFORE any ranking the whole catalogue is the coloured "market". The moment a
 * ranking arrives, every catalogue cell the backend DIDN'T rank greys out too —
 * the backend only ranks the narrowed set (search_cells + a 50-cell cap), so
 * without this the bulk of the market would stay falsely coloured and the plot
 * would never visibly narrow as the user adds requirements.
 *
 * Axes are FIXED to the full catalogue extent (per Basis/Domain) and do NOT
 * snap inward as candidates narrow. A Focus-View toggle hides the ruled-out /
 * not-a-candidate cells and zooms onto the contenders. Clicking ANY point —
 * even a greyed one — pins it (manual override) so the customer keeps the
 * freedom to add a cell Ion filtered out.
 */

import { useEffect, useRef } from 'preact/hooks'
import * as echarts from 'echarts'

import { basis, domain } from '../../../state/globalSignals.js'
import {
  canvasFocusView,
  manualPicks,
  selectionContext,
  toggleManualPick,
  currentConversationId,
} from '../../../state/chatStore.js'
import { ragoneAxisMeta, resolveXY, resolveThreshold } from '../../visualizations/ragoneAxes.js'
import {
  catalogueCells,
  catalogueStatus,
  ensureCatalogue,
  retryCatalogue,
  cellRagoneXY,
  cellPointId,
} from './ragoneCatalogue.js'
import { recordManualCellAdd } from '../../../services/selectionFlowService.js'
import { getCategoryColor } from '../../../utils/plotColors.js'
import { getWatermarkGraphic } from '../../../utils/chartWatermark.js'
import { generateSlugFromCell } from '../../../utils/cellSlugMap.js'
import { t } from '../../../i18n/index.js'

const BRAND_ORANGE = '#ea5b0c'
const EXCLUDED_GREY = 'rgba(148, 163, 184, 0.45)' // ruled out, still visible
const MANUAL_PIN = '#ea5b0c'
const TEXT_PRIMARY = '#f1f5f9'
const TEXT_TERTIARY = '#94a3b8'
const COLOR_BY = 'chemistry' // mirror the Explorer Ragone's default colouring

const GRID = { left: 64, right: 36, top: 16, bottom: 64, containLabel: true }

/** Stages at which the Top-N shortlist ranking is surfaced. Before this, all
 *  passing candidates look the same — only ruling-out happens. */
const RANKING_STAGES = new Set(['shortlist', 'trade_offs'])

function isValidPoint(p) {
  return (
    p &&
    Number.isFinite(p.x) &&
    Number.isFinite(p.y) &&
    typeof p.manufacturer === 'string' &&
    typeof p.name === 'string'
  )
}

/** Stable identity for ECharts data-diffing + catalogue/overlay joins. */
export function pointId(p) {
  return `${p && p.manufacturer}::${p && p.name}`
}

/** A candidate that failed a hard filter (backend sets passed:false). */
export function isExcluded(p) {
  return !!p && p.passed === false
}

/** Chemistry category for colouring — mirrors RagonePlotECharts.getCategoryName
 *  for colorBy='chemistry'. */
export function cellCategory(cell) {
  const c = (cell && cell.chemical) || {}
  const v = c.cathode_material_group || c.main_chemistry || c.cathode || (cell && cell.technology)
  if (!v || String(v).trim() === '') return 'Unknown'
  return String(v).trim()
}

function cellFormat(cell) {
  const m = (cell && cell.mechanical) || {}
  return m.format_group || m.format || null
}

function prefersReducedMotion() {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

const round2 = (v) => Math.round(v * 100) / 100

/**
 * Index the backend plot_data.points by identity → { passed, rank }. Exact
 * match plus a normalized fallback so minor name drift still joins. Exported
 * for tests.
 */
export function buildOverlayIndex(plotData) {
  const exact = new Map()
  const normalized = new Map()
  const points = plotData && Array.isArray(plotData.points) ? plotData.points : []
  for (const p of points) {
    if (!isValidPoint(p)) continue
    const entry = { passed: p.passed, rank: p.rank, point: p }
    exact.set(pointId(p), entry)
    normalized.set(normId(p.manufacturer, p.name), entry)
  }
  return { exact, normalized }
}

function normId(manufacturer, name) {
  const norm = (s) => String(s || '').toLowerCase().trim().replace(/\s+/g, ' ')
  return `${norm(manufacturer)}::${norm(name)}`
}

function overlayFor(overlay, manufacturer, name) {
  const id = `${manufacturer}::${name}`
  return overlay.exact.get(id) || overlay.normalized.get(normId(manufacturer, name)) || null
}

/**
 * Tight axis bounds around the given coords (with ~8% padding). Returns null
 * when there's nothing finite to frame. Pure — exported for tests. Used for the
 * fixed full-extent (all cells, stable across re-ranks) and the focus view.
 */
export function computeBounds(coords) {
  const xs = coords.map((c) => c.x).filter(Number.isFinite)
  const ys = coords.map((c) => c.y).filter(Number.isFinite)
  if (!xs.length || !ys.length) return null
  const pad = (lo, hi) => {
    const span = hi - lo
    const p = span > 0 ? span * 0.08 : Math.max(Math.abs(hi) * 0.08, 1)
    return [lo - p, hi + p]
  }
  const [xMin, xMax] = pad(Math.min(...xs), Math.max(...xs))
  const [yMin, yMax] = pad(Math.min(...ys), Math.max(...ys))
  return { xMin, xMax, yMin, yMax }
}

/**
 * The live caption: "28 of 44 cells match — 16 removed: …". Falls back to a
 * plain match-count, then null. Pure — exported for tests.
 */
export function formatExclusionCaption(plotData) {
  if (!plotData) return null
  const { exclusions, matched, total } = plotData
  const hasCounts = Number.isFinite(matched) && Number.isFinite(total) && total > 0
  if (Array.isArray(exclusions) && exclusions.length && hasCounts) {
    const reasons = exclusions
      .filter((e) => e && e.removed > 0 && e.label)
      .map((e) => `${e.removed} removed: ${e.label}`)
      .join(' · ')
    if (reasons) return `${matched} of ${total} cells match — ${reasons}`
  }
  if (hasCounts) return `${matched} of ${total} cells match your requirements`
  return null
}

const BASIS_LABEL = { abs: 'ABS', grav: 'GRAV', vol: 'VOL' }
const DOMAIN_LABEL = {
  'energy-power': 'Energy / Power',
  'capacity-current': 'Capacity / Current',
}

/**
 * When the plot has threshold lines but NONE resolve for the current toggle,
 * tell the user which view shows the line. Pure — exported for tests.
 */
export function requirementHint(plotData, basisKey, domainKey) {
  const thresholds = (plotData && plotData.thresholds) || []
  if (!thresholds.length) return null
  if (thresholds.some((t) => Number.isFinite(resolveThreshold(t, basisKey, domainKey)))) {
    return null
  }
  for (const t of thresholds) {
    const c = t && t.coords
    if (!c) continue
    for (const dom of Object.keys(c)) {
      for (const b of Object.keys(c[dom])) {
        const basisL = BASIS_LABEL[b] || String(b).toUpperCase()
        const where =
          dom !== domainKey && DOMAIN_LABEL[dom] ? `${DOMAIN_LABEL[dom]} · ${basisL}` : basisL
        return `Requirement line shown in the ${where} view`
      }
    }
  }
  return null
}

/**
 * Join the full catalogue with the backend overlay into per-tier point arrays.
 * Pure — exported for tests. Each datum carries identity, coords, the chemistry
 * `category` (for Explorer-style colouring), a `slug` (for the tooltip link),
 * and the overlay `passed`/`rank`.
 *
 * Tier rules:
 *   - manual pick (by id)            → `manual`
 *   - not in overlay                 → `notAssessed` (still "in play", coloured)
 *   - overlay passed === false       → `excluded` (greyed)
 *   - overlay passed (true/absent)   → `top` if ranking on + rank<=topN, else `candidate`
 * Backend overlay points absent from the catalogue are appended (synthetic).
 */
export function joinCatalogueWithOverlay({
  catalogue = [],
  plotData = null,
  basisKey = 'grav',
  domainKey = 'energy-power',
  manualPickIds = [],
  stage = null,
}) {
  const overlay = buildOverlayIndex(plotData)
  const topN = plotData && Number.isFinite(plotData.top_n) ? plotData.top_n : 5
  const rankingOn = RANKING_STAGES.has(stage)
  // A ranking exists once the backend has returned at least one overlay point.
  // Before that we're still showing the full coloured market; after it, any
  // catalogue cell the backend DIDN'T rank is not a current candidate.
  const hasOverlay =
    !!plotData && Array.isArray(plotData.points) && plotData.points.length > 0
  const manualSet = new Set(manualPickIds)
  const tiers = { notAssessed: [], excluded: [], candidate: [], top: [], manual: [] }
  const allXY = []
  const focusXY = []
  const seen = new Set()

  const place = (datum, xy, passed, rank) => {
    allXY.push(xy)
    if (manualSet.has(datum.id)) {
      tiers.manual.push(datum)
      focusXY.push(xy)
      return
    }
    if (passed === false) {
      tiers.excluded.push(datum)
      return
    }
    if (passed === undefined) {
      // A catalogue cell the backend didn't rank. BEFORE any ranking (no overlay)
      // it's part of the coloured full-market overview. ONCE a ranking exists it
      // is NOT a current candidate — the backend only ranks the narrowed set that
      // survived search_cells + the 50-cell cap — so it dims like a ruled-out
      // cell, and the plot visibly narrows as the user adds requirements. Either
      // way it's not a contender → kept out of focusXY.
      if (hasOverlay) {
        datum.offCandidate = true
        tiers.excluded.push(datum)
      } else {
        tiers.notAssessed.push(datum)
      }
      return
    }
    // passed === true → a contender (top-N highlighted only at the shortlist).
    focusXY.push(xy)
    if (rankingOn && Number.isFinite(rank) && rank <= topN) tiers.top.push(datum)
    else tiers.candidate.push(datum)
  }

  for (const cell of catalogue) {
    const name = cell && (cell.name || cell.model)
    const xy = cellRagoneXY(cell, basisKey, domainKey)
    if (!xy) continue
    const id = cellPointId(cell)
    seen.add(id)
    const o = overlayFor(overlay, cell.manufacturer, name)
    const datum = {
      id,
      value: [xy.x, xy.y],
      x: xy.x,
      y: xy.y,
      manufacturer: cell.manufacturer,
      name,
      category: cellCategory(cell),
      format: cellFormat(cell),
      slug: generateSlugFromCell({ manufacturer: cell.manufacturer, name }),
      rank: o ? o.rank : undefined,
      passed: o ? o.passed : undefined,
    }
    place(datum, xy, datum.passed, datum.rank, true)
  }

  // Synthetic overlay points not present in the catalogue.
  const points = plotData && Array.isArray(plotData.points) ? plotData.points : []
  for (const p of points) {
    if (!isValidPoint(p)) continue
    const id = pointId(p)
    if (seen.has(id)) continue
    seen.add(id)
    const c = resolveXY(p, basisKey, domainKey)
    if (!Number.isFinite(c.x) || !Number.isFinite(c.y)) continue
    const xy = { x: c.x, y: c.y }
    const datum = {
      id,
      value: [c.x, c.y],
      x: c.x,
      y: c.y,
      manufacturer: p.manufacturer,
      name: p.name,
      category: 'Unknown',
      format: null,
      slug: generateSlugFromCell({ manufacturer: p.manufacturer, name: p.name }),
      rank: p.rank,
      passed: p.passed,
    }
    place(datum, xy, p.passed, p.rank, false)
  }

  return { tiers, allXY, focusXY, topN, rankingOn }
}

function escapeHtml(s) {
  return String(s || '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[c])
}

function axisLabelFormatter(v) {
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`
  if (v >= 100) return v.toFixed(0)
  return v.toFixed(1)
}

/**
 * Build the ECharts option. Pure (no signals). Exported for tests.
 * @param {object} plotData
 * @param {string} basisKey
 * @param {string} domainKey
 * @param {object} [opts] { catalogue, manualPickIds, stage, focusView }
 */
export function buildOption(plotData, basisKey = 'grav', domainKey = 'energy-power', opts = {}) {
  const { catalogue = [], manualPickIds = [], stage = null, focusView = false } = opts
  const { tiers, allXY, focusXY } = joinCatalogueWithOverlay({
    catalogue,
    plotData,
    basisKey,
    domainKey,
    manualPickIds,
    stage,
  })

  const hasCoords =
    (plotData && Array.isArray(plotData.points) && plotData.points.some((p) => p && p.coords)) ||
    catalogue.length > 0
  const meta = ragoneAxisMeta(basisKey, domainKey)
  const xName = hasCoords ? `${meta.xLabel} [${meta.xUnit}]` : (plotData && plotData.x_label) || ''
  const yName = hasCoords ? `${meta.yLabel} [${meta.yUnit}]` : (plotData && plotData.y_label) || ''

  // Threshold requirement LINES — resolve to a scalar for the current toggle.
  const lines = ((plotData && plotData.thresholds) || [])
    .map((thr) => ({ t: thr, pos: resolveThreshold(thr, basisKey, domainKey) }))
    .filter((l) => Number.isFinite(l.pos))

  const focusCoords = [...focusXY]
  for (const l of lines) {
    focusCoords.push(l.t.axis === 'x' ? { x: l.pos, y: NaN } : { x: NaN, y: l.pos })
  }
  const bounds = focusView ? computeBounds(focusCoords) || computeBounds(allXY) : computeBounds(allXY)
  const axisRange = (which) =>
    bounds
      ? which === 'x'
        ? { min: round2(bounds.xMin), max: round2(bounds.xMax) }
        : { min: round2(bounds.yMin), max: round2(bounds.yMax) }
      : {}

  const markLineData = lines.map((l) => ({
    name: l.t.label,
    [l.t.axis === 'x' ? 'xAxis' : 'yAxis']: l.pos,
    label: { position: 'insideEndTop' },
  }))
  const markAreaData = lines
    .filter((l) => l.t.op !== '==')
    .map((l) => {
      const axisKey = l.t.axis === 'x' ? 'xAxis' : 'yAxis'
      const lowInfeasible = l.t.op === '>=' || l.t.op === '>'
      return lowInfeasible
        ? [{ [axisKey]: 'min' }, { [axisKey]: l.pos }]
        : [{ [axisKey]: l.pos }, { [axisKey]: 'max' }]
    })
  const thresholdSeries = {
    id: '__thresholds',
    name: '__thresholds',
    type: 'line',
    data: [],
    z: 0,
    silent: true,
    markLine: {
      silent: true,
      symbol: ['none', 'none'],
      lineStyle: { color: BRAND_ORANGE, type: 'dashed', width: 1.5 },
      label: { show: true, color: TEXT_TERTIARY, fontSize: 10, formatter: (p) => p.name },
      data: markLineData,
    },
    markArea: {
      silent: true,
      itemStyle: { color: 'rgba(234, 91, 12, 0.06)' },
      data: markAreaData,
    },
  }

  // In focus view the ruled-out + not-yet-assessed cells are hidden.
  const inPlay = focusView
    ? tiers.candidate
    : [...tiers.notAssessed, ...tiers.candidate]
  const excluded = focusView ? [] : tiers.excluded

  // The "market" — coloured by chemistry, ONE series per category (so the
  // legend matches the Explorer Ragone).
  const byCategory = {}
  for (const d of inPlay) (byCategory[d.category] = byCategory[d.category] || []).push(d)
  const categories = Object.keys(byCategory).sort((a, b) =>
    a === 'Unknown' ? 1 : b === 'Unknown' ? -1 : a.localeCompare(b),
  )
  const categorySeries = categories.map((cat) => ({
    // Stable id per chemistry category so ECharts matches series across
    // re-ranks (with replaceMerge) and morphs points instead of clearing the
    // layer — the "disappear then reappear" flash otherwise happens because the
    // series ARRAY changes shape every re-rank (categories come and go).
    id: `cat:${cat}`,
    name: cat,
    type: 'scatter',
    symbolSize: 8,
    itemStyle: { color: getCategoryColor(COLOR_BY, cat), opacity: 0.85, borderWidth: 0 },
    large: byCategory[cat].length > 200,
    largeThreshold: 200,
    universalTransition: { enabled: true },
    data: byCategory[cat],
    z: 2,
  }))

  // Top picks — keep the chemistry colour but add a white ring + glow + label.
  const topData = tiers.top.map((d) => ({
    ...d,
    seriesTier: 'top',
    itemStyle: {
      color: getCategoryColor(COLOR_BY, d.category),
      borderColor: '#fff',
      borderWidth: 2,
      shadowBlur: 8,
      shadowColor: 'rgba(234, 91, 12, 0.8)',
    },
  }))

  const legendData = [
    ...categories,
    ...(excluded.length ? ['Ruled out'] : []),
    ...(topData.length ? ['Top picks'] : []),
  ]

  return {
    grid: GRID,
    backgroundColor: 'transparent',
    animation: !prefersReducedMotion(),
    animationDuration: 500,
    animationDurationUpdate: 450,
    animationEasingUpdate: 'cubicOut',
    graphic: [
      getWatermarkGraphic({ position: 'top-right', opacity: 0.1, width: 180, height: 46, grid: GRID }),
    ],
    tooltip: {
      trigger: 'item',
      enterable: true,
      confine: true,
      backgroundColor: 'rgba(15, 23, 42, 0.96)',
      borderColor: 'rgba(255, 255, 255, 0.08)',
      borderWidth: 1,
      textStyle: { color: TEXT_PRIMARY, fontFamily: 'Roboto, sans-serif', fontSize: 12 },
      padding: [10, 14],
      formatter: (p) => {
        const cell = p.data || {}
        const isTop = cell.seriesTier === 'top'
        const rankRow = isTop && Number.isFinite(cell.rank)
          ? `<div style="color:${BRAND_ORANGE};font-weight:600;margin-bottom:4px;">#${cell.rank} shortlist</div>`
          : ''
        const meta2 = cell.category
          ? `<div style="color:${TEXT_TERTIARY};font-size:11px;margin-top:4px;">${escapeHtml(cell.category)}${cell.format ? ' • ' + escapeHtml(cell.format) : ''}</div>`
          : ''
        const stateRow =
          cell.passed === false
            ? `<div style="color:${TEXT_TERTIARY};font-size:11px;margin-top:6px;">Ruled out by your requirements — click to add anyway</div>`
            : cell.offCandidate
              ? `<div style="color:${TEXT_TERTIARY};font-size:11px;margin-top:6px;">Outside your current requirements — click to add anyway</div>`
              : `<div style="color:${TEXT_TERTIARY};font-size:11px;margin-top:6px;">Click the cell to pin it to your shortlist</div>`
        // Defence-in-depth: only emit the handler when the slug is strictly
        // [a-z0-9-] (generateSlugFromCell already guarantees this), so nothing
        // can break out of the onclick JS-string. The text fields above are all
        // escapeHtml'd; numbers go through round2.
        const safeSlug = typeof cell.slug === 'string' && /^[a-z0-9-]+$/.test(cell.slug)
        const viewBtn = safeSlug
          ? `<div style="margin-top:8px;"><button onclick="window.__ionViewCell && window.__ionViewCell('${cell.slug}')" style="padding:5px 10px;background:rgba(234,91,12,0.15);border:1px solid rgba(234,91,12,0.4);border-radius:4px;color:${BRAND_ORANGE};font-family:'Roboto',sans-serif;font-size:11px;font-weight:500;cursor:pointer;">View details →</button></div>`
          : ''
        return `
          <div style="font-family:'Big John',sans-serif;font-size:13px;font-weight:600;color:${TEXT_PRIMARY};text-transform:uppercase;">
            ${escapeHtml(cell.manufacturer)} ${escapeHtml(cell.name)}
          </div>
          ${rankRow}
          <div style="color:${TEXT_TERTIARY};font-size:11px;margin-top:4px;">
            ${escapeHtml(xName || 'X')}: <span style="color:${TEXT_PRIMARY};">${round2(cell.x)}</span><br/>
            ${escapeHtml(yName || 'Y')}: <span style="color:${TEXT_PRIMARY};">${round2(cell.y)}</span>
          </div>
          ${meta2}
          ${stateRow}
          ${viewBtn}
        `
      },
    },
    legend: {
      type: 'scroll',
      data: legendData,
      bottom: 0,
      itemWidth: 11,
      itemHeight: 11,
      itemGap: 14,
      textStyle: { color: TEXT_TERTIARY, fontSize: 11 },
      pageIconColor: BRAND_ORANGE,
      pageTextStyle: { color: TEXT_TERTIARY },
    },
    xAxis: {
      type: 'value',
      name: xName,
      nameLocation: 'middle',
      nameGap: 36,
      nameTextStyle: { color: TEXT_TERTIARY, fontSize: 12, fontFamily: 'Roboto, sans-serif' },
      axisLine: { lineStyle: { color: 'rgba(255,255,255,0.15)' } },
      axisLabel: { color: TEXT_TERTIARY, fontSize: 11, formatter: axisLabelFormatter },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)', type: [4, 4] } },
      ...axisRange('x'),
    },
    yAxis: {
      type: 'value',
      name: yName,
      nameLocation: 'middle',
      nameGap: 52,
      nameTextStyle: { color: TEXT_TERTIARY, fontSize: 12, fontFamily: 'Roboto, sans-serif' },
      axisLine: { lineStyle: { color: 'rgba(255,255,255,0.15)' } },
      axisLabel: { color: TEXT_TERTIARY, fontSize: 11, formatter: axisLabelFormatter },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)', type: [4, 4] } },
      ...axisRange('y'),
    },
    series: [
      ...categorySeries,
      {
        id: 'ruled-out',
        name: 'Ruled out',
        type: 'scatter',
        symbolSize: 7,
        itemStyle: { color: EXCLUDED_GREY, borderWidth: 0 },
        large: excluded.length > 200,
        largeThreshold: 200,
        universalTransition: { enabled: true },
        data: excluded,
        z: 1,
      },
      {
        id: 'top-picks',
        name: 'Top picks',
        type: 'scatter',
        symbolSize: 15,
        // Per-datum itemStyle keeps each top pick its chemistry colour; this
        // series-level colour is just the legend swatch.
        itemStyle: { color: BRAND_ORANGE },
        label: {
          show: true,
          position: 'top',
          distance: 6,
          color: TEXT_PRIMARY,
          fontFamily: 'Big John, sans-serif',
          fontSize: 11,
          fontWeight: 600,
          formatter: (p) => p.data.name,
        },
        labelLayout: { moveOverlap: 'shiftY', hideOverlap: false },
        universalTransition: { enabled: true },
        data: topData,
        z: 5,
      },
      {
        id: 'your-picks',
        name: 'Your picks',
        type: 'scatter',
        symbol: 'pin',
        symbolSize: 26,
        universalTransition: { enabled: true },
        itemStyle: { color: MANUAL_PIN, borderColor: '#fff', borderWidth: 1 },
        label: {
          show: true,
          position: 'top',
          distance: 8,
          color: TEXT_PRIMARY,
          fontFamily: 'Big John, sans-serif',
          fontSize: 11,
          fontWeight: 600,
          formatter: (p) => p.data.name,
        },
        data: tiers.manual.map((d) => ({ ...d, seriesTier: 'manual' })),
        z: 6,
      },
      thresholdSeries,
    ],
  }
}

function passingIdSet(plotData) {
  const points = plotData && Array.isArray(plotData.points) ? plotData.points : []
  return new Set(points.filter((p) => isValidPoint(p) && p.passed !== false).map(pointId))
}

/**
 * @param {object} props
 * @param {object} props.plotData
 * @param {string} [props.caption]
 */
/**
 * Pure: assemble the reduction-funnel rows from plot_data — Full catalogue →
 * After reduce → Meets requirements → Top picks. Rows with no backing number
 * are dropped (the funnel only appears once the backend supplies catalogue
 * counts). Exported for tests.
 */
function buildFunnelRows(plotData) {
  const f = plotData && plotData.funnel
  const total = f && typeof f.catalogue_total === 'number' ? f.catalogue_total : null
  if (total === null) return []
  const rows = [{ key: 'catalogue', labelKey: 'ion.funnel.catalogue', count: total }]
  if (f && typeof f.after_reduce === 'number') {
    rows.push({ key: 'reduce', labelKey: 'ion.funnel.reduce', count: f.after_reduce })
  }
  // The pool Ion actually ranks (top-relevant, capped) — makes the drop to
  // "meets requirements" honest: the pack/numeric gates are evaluated on THIS
  // set, not the whole after-reduce catalogue. `total` = plotted candidates.
  if (typeof plotData.total === 'number') {
    rows.push({ key: 'considered', labelKey: 'ion.funnel.considered', count: plotData.total })
  }
  if (typeof plotData.matched === 'number') {
    rows.push({ key: 'requirement', labelKey: 'ion.funnel.requirement', count: plotData.matched })
  }
  if (typeof plotData.top_n === 'number') {
    rows.push({ key: 'top', labelKey: 'ion.funnel.top', count: plotData.top_n, top: true })
  }
  return rows
}

/**
 * Compact reduction funnel under the plot: how the whole catalogue narrows to
 * the shortlist (phase-aligned with the breadcrumb — catalogue → reduce →
 * requirements → top). Bars are proportional to the catalogue size.
 */
function SalesFunnel({ plotData }) {
  const rows = buildFunnelRows(plotData)
  // Need at least the catalogue + one narrowing step to be worth showing.
  if (rows.length < 2) return null
  const max = rows[0].count || 1
  return (
    <ol class="ion-ragone-funnel" aria-label={t('ion.funnel.aria')}>
      {rows.map((r) => {
        const pct = Math.max(3, Math.min(100, Math.round((r.count / max) * 100)))
        return (
          <li
            key={r.key}
            class={`ion-ragone-funnel__row${r.top ? ' ion-ragone-funnel__row--top' : ''}`}
          >
            <span class="ion-ragone-funnel__label">{t(r.labelKey)}</span>
            <span class="ion-ragone-funnel__track" aria-hidden="true">
              <span class="ion-ragone-funnel__fill" style={`width:${pct}%`} />
            </span>
            <span class="ion-ragone-funnel__count">{r.count}</span>
          </li>
        )
      })}
    </ol>
  )
}

export function SalesRagonePlot({ plotData, caption }) {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const stateRef = useRef(null)

  const basisKey = basis.value
  const domainKey = domain.value
  const focusView = canvasFocusView.value
  const picks = manualPicks.value
  const stage = selectionContext.value.stage
  const catalogue = catalogueCells.value
  const catStatus = catalogueStatus.value

  const manualPickIds = picks.map((p) => p.pointId)
  const buildOpts = { catalogue, manualPickIds, stage, focusView }
  stateRef.current = { plotData, basisKey, domainKey, buildOpts }

  // Seed the full catalogue once (idempotent / deduped).
  useEffect(() => {
    ensureCatalogue()
  }, [])

  // Create the chart ONCE; re-ranks reuse the same instance (setOption).
  useEffect(() => {
    let initialised = false
    // Tooltip "View details" link — opens the cell's factsheet (same URL shape
    // as the Explorer plot). Slug is alphanumeric so it's safe in the handler.
    if (typeof window !== 'undefined') {
      window.__ionViewCell = (slug) => {
        if (slug) window.open(`/explorer?cell=${slug}&section=factsheet`, '_blank', 'noopener,noreferrer')
      }
    }

    const ensureInit = () => {
      if (initialised || !containerRef.current) return
      const { clientWidth, clientHeight } = containerRef.current
      if (clientWidth === 0 || clientHeight === 0) return
      chartRef.current = echarts.init(containerRef.current, null, {
        renderer: 'canvas',
        useDirtyRect: true,
      })
      initialised = true
      const s = stateRef.current
      chartRef.current.setOption(buildOption(s.plotData, s.basisKey, s.domainKey, s.buildOpts), {
        notMerge: true,
      })
      // Click ANY point (even a greyed/ruled-out one) to pin it — manual
      // override so the customer can add a cell Ion filtered out.
      chartRef.current.on('click', (params) => {
        const d = params && params.data
        if (!d || !d.id || typeof d.manufacturer !== 'string') return
        const adding = !manualPicks.peek().some((p) => p.pointId === d.id)
        toggleManualPick({
          pointId: d.id,
          manufacturer: d.manufacturer,
          name: d.name,
          originalPassed: d.passed !== false,
          exclusionReason: d.passed === false ? 'ruled out by your requirements' : null,
          pinnedAt: Date.now(),
        })
        if (adding) {
          recordManualCellAdd(
            currentConversationId.peek(),
            { manufacturer: d.manufacturer, name: d.name },
            d.passed === false ? 'ruled out by requirements' : null,
          )
        }
      })
    }

    ensureInit()

    const resizeObserver = new ResizeObserver(() => {
      ensureInit()
      chartRef.current?.resize()
    })
    resizeObserver.observe(containerRef.current)

    const onWindowResize = () => chartRef.current?.resize()
    // Same `typeof window` guard as the tooltip bridge at the top of this effect.
    // In a browser it is always true; under vitest a preact after-paint flush can
    // run this effect (and its cleanup) after jsdom teardown has removed the
    // global, and a bare `window` reference then throws a ReferenceError that
    // preact rethrows as an uncaught error — reddening a run in which every test
    // passed (pipeline 2564 lineage; see frontend/tests/setup.js).
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onWindowResize)
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onWindowResize)
      }
      resizeObserver.disconnect()
      if (chartRef.current) {
        chartRef.current.dispose()
        chartRef.current = null
      }
    }
  }, [])

  // Re-plot on any state change. `replaceMerge: ['series']` fully swaps the
  // (variable-length) series array while merging grid/axes/legend — combined
  // with the stable per-series `id`s above and universalTransition, ECharts
  // now MORPHS points (fly / fade / grey-out) across a re-rank instead of
  // clearing and redrawing the layer (the old merge-by-index path mismatched
  // series whenever a chemistry category appeared/disappeared → the flash).
  // Mirrors the Cell-Explorer Ragone (RagonePlotECharts replaceMerge:['series']).
  useEffect(() => {
    if (!chartRef.current) return
    chartRef.current.setOption(buildOption(plotData, basisKey, domainKey, buildOpts), {
      replaceMerge: ['series'],
    })
  }, [plotData, basisKey, domainKey, focusView, picks, stage, catalogue])

  const hasCatalogue = Array.isArray(catalogue) && catalogue.length > 0
  const hasOverlay = plotData && Array.isArray(plotData.points) && plotData.points.length > 0
  // Catalogue is the source of the full ~315-cell shadow population. Until it is
  // ready we must not settle on an overlay-only (~22 demo cells) render — that is
  // exactly Bug D. Treat "catalogue not ready yet" as a loading state even when
  // an overlay has already arrived.
  const catalogueReady = catStatus === 'ready' && hasCatalogue
  const catalogueLoading = !hasCatalogue && (catStatus === 'loading' || catStatus === 'idle')
  const catalogueFailed = !hasCatalogue && catStatus === 'error'
  const isEmpty = !hasCatalogue && !hasOverlay && !catalogueLoading
  // NOTE: the retry button below only renders while catStatus === 'error'. Once
  // retryCatalogue() fires, status flips to 'loading' and the button unmounts —
  // so a stale-load clobber (Task 1 reviewer's Minor note) can only happen on a
  // same-tick double-click. Guard it: make the onClick a no-op unless still
  // failed.
  const captionText = isEmpty ? null : formatExclusionCaption(plotData) || caption
  const axisFixed = !isEmpty && !!(plotData && plotData.axis_mode)
  const hintText = isEmpty ? null : requirementHint(plotData, basisKey, domainKey)

  return (
    <figure class="ion-ragone-plot">
      <div class="ion-ragone-plot__toolbar">
        <span class="ion-ragone-plot__hint">{t('ion.plot.pin_hint')}</span>
        <label class="ion-ragone-plot__focus">
          <input
            type="checkbox"
            checked={focusView}
            disabled={!hasOverlay}
            onChange={(e) => {
              canvasFocusView.value = e.target.checked
            }}
          />
          <span>{t('ion.plot.focus')}</span>
        </label>
      </div>
      {!catalogueReady && (catalogueLoading || catalogueFailed) && (
        <div class="ion-ragone-plot__catalogue-state" role="status">
          {catalogueLoading && <span>Loading the full cell catalogue…</span>}
          {catalogueFailed && (
            <button
              type="button"
              class="ion-ragone-plot__retry"
              onClick={() => {
                if (catalogueStatus.value === 'error') retryCatalogue()
              }}
            >
              Couldn’t load the full catalogue — retry
            </button>
          )}
        </div>
      )}
      <div class="ion-ragone-plot__stage">
        <div
          ref={containerRef}
          class="ion-ragone-plot__canvas"
          aria-label="Cell-selection Ragone scatter plot"
          aria-hidden={isEmpty ? 'true' : undefined}
        />
        {isEmpty && <p class="ion-ragone-plot__empty-overlay">No plot data available yet.</p>}
      </div>
      {axisFixed && (
        <p class="ion-ragone-plot__axis-note">
          Axes fixed for this branch — the Basis / Domain toggle doesn't apply here.
        </p>
      )}
      {hintText && <p class="ion-ragone-plot__axis-note">{hintText}</p>}
      {captionText && (
        <figcaption class="ion-ragone-plot__caption" aria-live="polite">
          <span key={captionText} class="ion-ragone-plot__caption-text">
            {captionText}
          </span>
        </figcaption>
      )}
      {!isEmpty && <SalesFunnel plotData={plotData} />}
    </figure>
  )
}

// Exported for tests
export const __test__ = {
  buildOption,
  buildOverlayIndex,
  joinCatalogueWithOverlay,
  escapeHtml,
  computeBounds,
  formatExclusionCaption,
  requirementHint,
  pointId,
  isExcluded,
  cellCategory,
  passingIdSet,
  buildFunnelRows,
}
